import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import type { Editor } from '@tiptap/core';
import {
  type ChangeEvent,
  type ReactNode,
  type UIEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { AppendicesSection } from '../appendices';
import { ProductsTab } from '../company/ProductsTab';
import { ServicesTab } from '../company/ServicesTab';
import { StageFeedbackView } from '../feedback/StageFeedbackView';
import { HighlightsSection, RisksSection } from '../highlights-and-risks';
import { OperatingPartnersTable } from '../talent/OperatingPartnersTable';
import { CashUseSection } from '../transaction-structure/CashUseSection';
import { Security } from '../transaction-structure/Security';

import FileInput from '@/components/FileInput';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Field, FieldContent, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';

export type Json =
  | { [key: string]: Json | undefined }
  | boolean
  | Json[]
  | null
  | number
  | string;

export type SduiFieldChangeHandler = (path: string, value: Json) => void;

export interface SduiItem {
  data?: Json;
  path?: string;
  resolve_by?: 'id' | 'value';
  select_options?: SduiSelectOption[];
  ui_component?: SduiUiComponent;
  value?: Json;
}

export interface SduiSelectOption {
  id?: string;
  name?: string;
  value?: number | string;
}

export interface SduiUiComponent {
  [key: string]: Json | undefined;
  component?: string;
  label?: string;
  props?: {
    [key: string]: Json | undefined;
    placeholder?: string;
  };
  type?: string;
}

const UPDATE_OPPORTUNITY_FROM_SDUI = gql`
  mutation UpdateOpportunityFromSdui(
    $opportunityId: ID!
    $values: [SduiFieldInput!]!
  ) {
    updateOpportunityFromSdui(opportunityId: $opportunityId, values: $values) {
      message
      ok
    }
  }
`;

export function SduiItemsRenderer({
  currentPageTitle,
  items,
  onChange
}: {
  currentPageTitle?: string;
  items: SduiItem[];
  onChange?: SduiFieldChangeHandler;
}) {
  const editable = typeof onChange === 'function';
  const { id: opportunityId } = useParams<{ id: string }>();
  const lastSentByPathRef = useRef<Record<string, string>>({});

  const [updateOpportunityFromSdui] = useMutation<{
    updateOpportunityFromSdui?: { message?: null | string; ok?: boolean };
  }>(UPDATE_OPPORTUNITY_FROM_SDUI);

  const commitFieldValue = useCallback(
    async (path: string, value: Json) => {
      if (!editable) return;
      if (!opportunityId) return;
      if (!path) return;

      const serialized = JSON.stringify(value);
      if (lastSentByPathRef.current[path] === serialized) return;
      lastSentByPathRef.current[path] = serialized;

      // Keep existing behavior for any parent that relies on onChange tracking.
      onChange(path, value);

      try {
        const { data } = await updateOpportunityFromSdui({
          variables: { opportunityId, values: [{ path, value }] }
        });
        const ok = data?.updateOpportunityFromSdui?.ok ?? false;
        if (!ok) {
          toast.error(
            data?.updateOpportunityFromSdui?.message ?? 'Update failed'
          );
        }
      } catch (error) {
        console.error('Failed to update opportunity from SDUI:', error);
        toast.error('Update failed');
      }
    },
    [editable, onChange, opportunityId, updateOpportunityFromSdui]
  );

  const sidebarOverride = useMemo(() => {
    if (!opportunityId) return null;
    if (!currentPageTitle) return null;
    return resolveSidebarOverrideFromPageTitle(opportunityId, currentPageTitle);
  }, [currentPageTitle, opportunityId]);

  if (sidebarOverride) {
    return (
      <div className="flex max-h-full min-w-0 flex-col overflow-y-auto">
        {sidebarOverride}
      </div>
    );
  }

  return (
    <div className="flex max-h-full flex-wrap gap-4 overflow-y-auto">
      {items.map((item, index) => {
        const type = item.ui_component?.type ?? 'unknown';
        const key = item.path ? `${item.path}_${String(index)}` : String(index);
        const widthClass = widthClassForType(type);
        const fieldProps = { item, onCommit: commitFieldValue };

        switch (type) {
          case 'custom_component':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <CustomComponentPlaceholderItem
                  item={item}
                  opportunityId={opportunityId as unknown as string}
                />
              </div>
            );
          case 'image':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <ImageFileInputItem {...fieldProps} />
              </div>
            );
          case 'multi_select_input':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <MultiSelectInputItem
                  {...fieldProps}
                  editable={editable}
                />
              </div>
            );
          case 'number_input':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <NumberInputItem
                  {...fieldProps}
                  editable={editable}
                />
              </div>
            );
          case 'percentage_input':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <PercentageInputItem
                  {...fieldProps}
                  editable={editable}
                />
              </div>
            );
          case 'rich_text':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <RichTextItem
                  {...fieldProps}
                  editable={editable}
                />
              </div>
            );
          case 'select_input':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <SelectInputItem
                  {...fieldProps}
                  editable={editable}
                />
              </div>
            );
          case 'string_input':
          case 'text_input':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <TextInputItem
                  {...fieldProps}
                  editable={editable}
                />
              </div>
            );
          case 'switch_input':
            return (
              <div
                className={`${widthClass} min-w-0 flex-none`}
                key={key}
              >
                <SwitchInputItem
                  {...fieldProps}
                  editable={editable}
                />
              </div>
            );
          default:
            return (
              <div
                className="w-full min-w-0 flex-none"
                key={key}
              >
                <UnknownItem item={item} />
              </div>
            );
        }
      })}
    </div>
  );
}

function CustomComponentPlaceholderItem({
  item,
  opportunityId
}: {
  item: SduiItem;
  opportunityId: string;
}) {
  const path = item.path ?? '';
  const label = item.ui_component?.label ?? path;
  const componentName = item.ui_component?.component ?? 'custom_component';
  const renderedComponent = useMemo(() => {
    switch (componentName) {
      case 'company_products_list':
        return <ProductsTab dealOpportunityId={opportunityId} />;
      case 'company_services_list':
        return <ServicesTab dealOpportunityId={opportunityId} />;
      case 'highlight_list':
        return <HighlightsSection opportunityId={opportunityId} />;
      case 'operating_partners_list':
        return <OperatingPartnersTable opportunityId={opportunityId} />;
      case 'opportunity_cash_use_list':
        return <CashUseSection dealOpportunityId={opportunityId} />;
      case 'risk_list':
        return <RisksSection opportunityId={opportunityId} />;
      case 'security_list':
        return <Security id={opportunityId} />;
      default:
        return (
          <div className="border-input dark:bg-input/30 text-muted-foreground rounded-md border bg-transparent px-3 py-2 text-sm">
            Placeholder for custom component:{' '}
            <span className="font-mono">{componentName}</span>
          </div>
        );
    }
  }, [componentName, opportunityId]);
  return (
    <Field>
      <FieldLabel className="w-full min-w-0">
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent>{renderedComponent}</FieldContent>
    </Field>
  );
}

function formatNumberWithThousandsCommas(value: '' | number): string {
  if (value === '' || !Number.isFinite(value)) return '';
  const s = String(value);
  const parts = s.split('.');
  const intPart = parts[0] ?? '';
  const decPart: string | undefined = parts[1];
  const isNeg = intPart.startsWith('-');
  const digits = isNeg ? intPart.slice(1) : intPart;
  const withCommas = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const full = isNeg ? `-${withCommas}` : withCommas;
  // decPart can be undefined when value has no decimal (e.g. 123 -> "123")
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime check for split result
  return decPart !== undefined ? `${full}.${decPart}` : full;
}

function ImageFileInputItem({
  item,
  onCommit
}: {
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const label = item.ui_component?.label ?? item.path ?? 'Image';
  const data = typeof item.value === 'string' ? item.value : undefined;
  const path = item.path ?? '';

  const handleChange = useCallback(
    (base64: null | string) => {
      onCommit?.(path, base64 ?? null);
    },
    [onCommit, path]
  );

  return (
    <Field>
      <FieldLabel className="w-full min-w-0">
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent>
        <FileInput
          data={data}
          id={path}
          name={path}
          onChange={handleChange}
        />
      </FieldContent>
    </Field>
  );
}

function normalizeNumberValue(value: unknown): '' | number {
  if (value === false || value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? value : '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const asNumber = Number(trimmed);
    return Number.isFinite(asNumber) ? asNumber : '';
  }
  return '';
}

function normalizeTextValue(value: unknown): string {
  if (value === false || value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return '';
}

function NumberInputItem({
  editable,
  item,
  onCommit
}: {
  editable: boolean;
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const path = item.path ?? '';
  const id = toDomId(path || 'sdui_number');
  const label = item.ui_component?.label ?? path;
  const placeholder = item.ui_component?.props?.placeholder ?? 'Enter Value';
  const initial = normalizeNumberValue(item.value);
  const initialText =
    initial === '' ? '' : formatNumberWithThousandsCommas(initial);

  const [localValue, setLocalValue] = useState(initialText);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setLocalValue(raw);
  };

  const handleBlur = () => {
    const num = parseNumberWithCommas(localValue);
    if (num !== null) {
      setLocalValue(formatNumberWithThousandsCommas(num));
    }
    if (!onCommit || !path) return;
    onCommit(path, num);
  };

  return (
    <Field>
      <FieldLabel
        className="w-full min-w-0"
        htmlFor={id}
      >
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent>
        <Input
          disabled={!editable}
          id={id}
          inputMode="decimal"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={placeholder}
          type="text"
          value={localValue}
        />
      </FieldContent>
    </Field>
  );
}

function parseNumberWithCommas(raw: string): null | number {
  const trimmed = raw.replace(/,/g, '').trim();
  if (trimmed === '') return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

function PercentageInputItem({
  editable,
  item,
  onCommit
}: {
  editable: boolean;
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const path = item.path ?? '';
  const id = toDomId(path || 'sdui_percentage');
  const label = item.ui_component?.label ?? path;
  const placeholder = item.ui_component?.props?.placeholder ?? 'Enter %';
  const initial = normalizeNumberValue(item.value);
  const displayValue = initial === '' ? '' : initial * 100;
  const initialText =
    displayValue === '' ? '' : formatNumberWithThousandsCommas(displayValue);

  const [localValue, setLocalValue] = useState(initialText);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setLocalValue(raw);
  };

  const handleBlur = () => {
    const num = parseNumberWithCommas(localValue);
    if (num !== null) {
      setLocalValue(formatNumberWithThousandsCommas(num));
    }
    if (!onCommit || !path) return;
    onCommit(path, num === null ? null : num / 100);
  };

  return (
    <Field>
      <FieldLabel
        className="w-full min-w-0"
        htmlFor={id}
      >
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent>
        <div className="relative flex items-center">
          <Input
            className="pr-8"
            disabled={!editable}
            id={id}
            inputMode="decimal"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={placeholder}
            type="text"
            value={localValue}
          />
          <span className="text-muted-foreground pointer-events-none absolute right-3 text-sm">
            %
          </span>
        </div>
      </FieldContent>
    </Field>
  );
}

function resolveSidebarOverrideFromPageTitle(
  opportunityId: string,
  title: string
): null | ReactNode {
  const lower = title.trim().toLowerCase();

  if (lower.includes('commentary from rsm')) {
    return (
      <StageFeedbackView
        opportunityId={opportunityId}
        reportType="rsm"
      />
    );
  }

  if (lower.includes('commentary from icb')) {
    return (
      <StageFeedbackView
        opportunityId={opportunityId}
        reportType="icb"
      />
    );
  }

  if (lower.includes('detailed investment highlights')) {
    return <HighlightsSection opportunityId={opportunityId} />;
  }

  if (
    lower.includes('detailed investment risks') &&
    lower.includes('mitigant')
  ) {
    return <RisksSection opportunityId={opportunityId} />;
  }

  if (lower.includes('appendix')) {
    const section = lower.includes('company appendix')
      ? 'company'
      : lower.includes('general appendix')
        ? 'general'
        : lower.includes('company appendices')
      ? 'company'
      : lower.includes('general appendices')
        ? 'general'
        : undefined;
    const appendixTitle = title.split(':')[1]?.trim() || undefined;

    return (
      <AppendicesSection
        opportunityId={opportunityId}
        section={section}
        title={appendixTitle}
        variant="embedded"
      />
    );
  }

  return null;
}

function RichTextItem({
  editable,
  item,
  onCommit
}: {
  editable: boolean;
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const path = item.path ?? '';
  const label = item.ui_component?.label ?? path;
  const initial = normalizeTextValue(item.value);
  const [localValue, setLocalValue] = useState(initial);
  const editorRef = useRef<Editor | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const flushToParent = useCallback(() => {
    const html = editorRef.current?.getHTML() ?? '';
    setLocalValue(html);
    if (onCommit && path) onCommit(path, html);
  }, [onCommit, path]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    el.addEventListener('focusout', flushToParent);
    return () => {
      el.removeEventListener('focusout', flushToParent);
    };
  }, [flushToParent]);

  if (editable) {
    return (
      <Field>
        <FieldLabel className="w-full min-w-0">
          <span className="block min-w-0 truncate">{label}</span>
        </FieldLabel>
        <FieldContent>
          <div ref={wrapperRef}>
            <RichTextField
              defaultValue={localValue}
              ref={editorRef}
            />
          </div>
        </FieldContent>
      </Field>
    );
  }

  return (
    <Field>
      <FieldLabel className="w-full min-w-0">
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent>
        <RichTextFieldReadOnly html={initial} />
      </FieldContent>
    </Field>
  );
}

const SELECT_PAGE_SIZE = 50;

interface NormalizedSelectOption {
  commit: number | string;
  key: string;
  label: string;
  search: string;
}

function MultiSelectInputItem({
  editable,
  item,
  onCommit
}: {
  editable: boolean;
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const path = item.path ?? '';
  const label = item.ui_component?.label ?? path;
  const placeholder = item.ui_component?.props?.placeholder ?? 'Select...';

  const options = useMemo(() => {
    return Array.isArray(item.select_options) ? item.select_options : [];
  }, [item.select_options]);

  const resolveBy: 'id' | 'value' = item.resolve_by === 'id' ? 'id' : 'value';

  const deriveSelectedKeys = useCallback((raw: Json | undefined): string[] => {
    if (Array.isArray(raw)) {
      return raw
        .map(v =>
          typeof v === 'number' || typeof v === 'string' ? String(v) : ''
        )
        .filter(Boolean);
    }
    if (raw == null) return [];
    return typeof raw === 'number' || typeof raw === 'string'
      ? [String(raw)]
      : [];
  }, []);

  const normalized = useMemo(() => {
    return options
      .map(opt => {
        const rawKey = resolveBy === 'id' ? opt.id : (opt.value ?? opt.id);
        const key = String(rawKey ?? '');
        if (!key) return null;

        const commit: number | string =
          resolveBy === 'id' ? (opt.id ?? key) : (opt.value ?? key);

        const optLabel = (opt.name ?? key).trim() || key;
        return {
          commit,
          key,
          label: optLabel,
          search: `${optLabel} ${key}`.toLowerCase()
        };
      })
      .filter((x): x is NormalizedSelectOption => x != null);
  }, [options, resolveBy]);

  const commitByKey = useMemo(() => {
    const map = new Map<string, NormalizedSelectOption['commit']>();
    for (const opt of normalized) map.set(opt.key, opt.commit);
    return map;
  }, [normalized]);

  const selectedKeysFromProps = useMemo(
    () => deriveSelectedKeys(item.value),
    [deriveSelectedKeys, item.value]
  );

  const selectedKeysFromPropsSerialized = useMemo(() => {
    return JSON.stringify(selectedKeysFromProps);
  }, [selectedKeysFromProps]);

  const [optimisticSelection, setOptimisticSelection] = useState<{
    baseSerialized: string;
    keys: string[];
  } | null>(null);

  const effectiveSelectedKeys =
    optimisticSelection?.baseSerialized === selectedKeysFromPropsSerialized
      ? optimisticSelection.keys
      : selectedKeysFromProps;

  const selectedSet = useMemo(
    () => new Set(effectiveSelectedKeys),
    [effectiveSelectedKeys]
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [renderLimit, setRenderLimit] = useState(SELECT_PAGE_SIZE);

  const q = query.trim().toLowerCase();
  const visibleItems = useMemo(() => {
    if (!q) return normalized.slice(0, renderLimit);
    return normalized.filter(it => it.search.includes(q)).slice(0, renderLimit);
  }, [normalized, q, renderLimit]);

  const handleScroll: UIEventHandler<HTMLDivElement> = e => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64) {
      setRenderLimit(l => l + SELECT_PAGE_SIZE);
    }
  };

  const toggle = (key: string) => {
    if (!editable) return;

    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);

    const nextKeys = Array.from(next);
    setOptimisticSelection({
      baseSerialized: selectedKeysFromPropsSerialized,
      keys: nextKeys
    });

    if (onCommit && path) {
      const nextArray = nextKeys.map(k => commitByKey.get(k) ?? k);
      onCommit(path, nextArray);
    }
  };

  const selectedLabels = useMemo(() => {
    return normalized
      .filter(it => selectedSet.has(it.key))
      .map(it => it.label)
      .join(', ');
  }, [normalized, selectedSet]);

  return (
    <Field>
      <FieldLabel className="w-full min-w-0 truncate">{label}</FieldLabel>
      <FieldContent>
        <Popover
          onOpenChange={next => {
            setOpen(next);
            if (!next) setQuery('');
            else setRenderLimit(SELECT_PAGE_SIZE);
          }}
          open={open}
        >
          <PopoverTrigger asChild>
            <Button
              className="w-full justify-between"
              disabled={!editable}
              type="button"
              variant="outline"
            >
              <span className="truncate">{selectedLabels || placeholder}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] p-0"
          >
            <Command shouldFilter={false}>
              <CommandInput
                onValueChange={setQuery}
                placeholder="Search..."
                value={query}
              />
              <CommandList
                className="max-h-[320px]"
                onScroll={handleScroll}
              >
                {visibleItems.length === 0 ? (
                  <CommandEmpty>No results</CommandEmpty>
                ) : (
                  visibleItems.map(it => (
                    <CommandItem
                      key={it.key}
                      onSelect={() => {
                        toggle(it.key);
                      }}
                      value={it.key}
                    >
                      <span className="truncate">
                        {selectedSet.has(it.key) ? '✓ ' : ''}
                        {it.label}
                      </span>
                    </CommandItem>
                  ))
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FieldContent>
    </Field>
  );
}

function SelectInputItem({
  editable,
  item,
  onCommit
}: {
  editable: boolean;
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const path = item.path ?? '';
  const label = item.ui_component?.label ?? path;
  const placeholder = item.ui_component?.props?.placeholder ?? 'Select';

  const options = useMemo(() => {
    return Array.isArray(item.select_options) ? item.select_options : [];
  }, [item.select_options]);

  const resolveBy: 'id' | 'value' = item.resolve_by === 'id' ? 'id' : 'value';

  const normalized = useMemo(() => {
    return options
      .map(opt => {
        const rawKey = resolveBy === 'id' ? opt.id : (opt.value ?? opt.id);
        const key = String(rawKey ?? '');
        if (!key) return null;

        const commit: number | string =
          resolveBy === 'id' ? (opt.id ?? key) : (opt.value ?? key);

        const optLabel = (opt.name ?? key).trim() || key;
        return {
          commit,
          key,
          label: optLabel,
          search: `${optLabel} ${key}`.toLowerCase()
        };
      })
      .filter((x): x is NormalizedSelectOption => x != null);
  }, [options, resolveBy]);

  const commitByKey = useMemo(() => {
    const map = new Map<string, NormalizedSelectOption['commit']>();
    for (const opt of normalized) map.set(opt.key, opt.commit);
    return map;
  }, [normalized]);

  const selectedValue = useMemo(() => {
    const currentValueRaw = item.value;
    const currentKey =
      typeof currentValueRaw === 'string' || typeof currentValueRaw === 'number'
        ? String(currentValueRaw)
        : undefined;
    if (!currentKey) return undefined;
    return normalized.some(o => o.key === currentKey) ? currentKey : undefined;
  }, [item.value, normalized]);

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of normalized) {
      map.set(opt.key, opt.label);
    }
    return map;
  }, [normalized]);

  const [userValue, setUserValue] = useState<string | undefined>(undefined);
  const value = userValue ?? selectedValue;
  const selectedLabel = value ? (labelByValue.get(value) ?? value) : undefined;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [renderLimit, setRenderLimit] = useState(SELECT_PAGE_SIZE);

  const handleValueChange = (value: string) => {
    setUserValue(value);
    if (onCommit && path) onCommit(path, commitByKey.get(value) ?? value);
  };

  const q = query.trim().toLowerCase();
  const visibleItems = useMemo(() => {
    if (!q) return normalized.slice(0, renderLimit);
    const out: { key: string; label: string }[] = [];
    for (const it of normalized) {
      if (it.search.includes(q)) out.push({ key: it.key, label: it.label });
      if (out.length >= renderLimit) break;
    }
    return out;
  }, [normalized, q, renderLimit]);

  const handleScroll: UIEventHandler<HTMLDivElement> = e => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64) {
      setRenderLimit(l => l + SELECT_PAGE_SIZE);
    }
  };

  return (
    <Field>
      <FieldLabel className="w-full min-w-0 truncate">{label}</FieldLabel>
      <FieldContent>
        <Popover
          onOpenChange={next => {
            setOpen(next);
            if (next) {
              setRenderLimit(SELECT_PAGE_SIZE);
            } else {
              setQuery('');
            }
          }}
          open={open}
        >
          <PopoverTrigger asChild>
            <Button
              className="w-full justify-between"
              disabled={!editable}
              type="button"
              variant="outline"
            >
              <span className="truncate">{selectedLabel ?? placeholder}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] p-0"
          >
            <Command shouldFilter={false}>
              <CommandInput
                onValueChange={setQuery}
                placeholder="Search..."
                value={query}
              />
              <CommandList
                className="max-h-[320px]"
                onScroll={handleScroll}
              >
                {visibleItems.length === 0 ? (
                  <CommandEmpty>No results</CommandEmpty>
                ) : (
                  visibleItems.map(it => (
                    <CommandItem
                      key={it.key}
                      onSelect={() => {
                        handleValueChange(it.key);
                        setOpen(false);
                      }}
                      value={it.key}
                    >
                      <span className="truncate">{it.label}</span>
                    </CommandItem>
                  ))
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FieldContent>
    </Field>
  );
}

function SwitchInputItem({
  editable,
  item,
  onCommit
}: {
  editable: boolean;
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const path = item.path ?? '';
  const id = toDomId(path || 'sdui_switch');
  const label = item.ui_component?.label ?? path;
  const initialChecked = switchValueToChecked(item.value ?? null);
  const [checked, setChecked] = useState(initialChecked);

  const handleCheckedChange = (next: boolean) => {
    setChecked(next);
    if (onCommit && path) onCommit(path, next);
  };

  return (
    <Field>
      <FieldLabel
        className="w-full min-w-0"
        htmlFor={id}
      >
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent className="flex min-h-8 justify-center">
        <Switch
          checked={checked}
          disabled={!editable}
          id={id}
          onCheckedChange={handleCheckedChange}
        />
      </FieldContent>
    </Field>
  );
}

function switchValueToChecked(value: Json): boolean {
  if (value === true || value === false) return value;
  if (value == null) return false;
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    if (lower === 'true' || lower === 'yes' || lower === '1') return true;
    if (lower === 'false' || lower === 'no' || lower === '0' || lower === '')
      return false;
  }
  if (typeof value === 'number') return value !== 0;
  return false;
}

function TextInputItem({
  editable,
  item,
  onCommit
}: {
  editable: boolean;
  item: SduiItem;
  onCommit?: SduiFieldChangeHandler;
}) {
  const path = item.path ?? '';
  const id = toDomId(path || 'sdui_text');
  const label = item.ui_component?.label ?? path;
  const placeholder = item.ui_component?.props?.placeholder ?? 'Enter Value';
  const initial = normalizeTextValue(item.value);

  const [localValue, setLocalValue] = useState(initial);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocalValue(v);
  };

  const handleBlur = () => {
    if (onCommit && path) onCommit(path, localValue);
  };

  return (
    <Field>
      <FieldLabel
        className="w-full min-w-0"
        htmlFor={id}
      >
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent>
        <Input
          disabled={!editable}
          id={id}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={placeholder}
          value={localValue}
        />
      </FieldContent>
    </Field>
  );
}

function toDomId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function UnknownItem({ item }: { item: SduiItem }) {
  const path = item.path ?? '';
  const label = item.ui_component?.label ?? path;
  const type = item.ui_component?.type ?? 'unknown';

  return (
    <Field>
      <FieldLabel className="w-full min-w-0">
        <span className="block min-w-0 truncate">{label}</span>
      </FieldLabel>
      <FieldContent>
        <div className="border-input dark:bg-input/30 text-muted-foreground rounded-md border bg-transparent px-3 py-2 text-sm">
          Unknown SDUI component type: <span className="font-mono">{type}</span>
        </div>
      </FieldContent>
    </Field>
  );
}

function widthClassForType(type: string) {
  if (type === 'custom_component') return 'w-full';
  if (type === 'rich_text') return 'w-full';
  if (type === 'image') return 'w-1/3';
  return 'w-full md:w-[calc((100%-2rem)/3)]';
}
